<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError } from '$shared/api';
  import { getUser, fetchCurrentUser } from '$lib/auth.svelte';
  import Avatar from '$shared/Avatar.svelte';

  const user = $derived(getUser());
  let username = $state('');
  let saving = $state(false);
  let message = $state('');
  let messageType = $state<'success' | 'error'>('success');
  let presets = $state<{ id: string; url: string }[]>([]);
  let currentAvatar = $state<string | null>(null);

  $effect(() => {
    if (user) {
      username = user.username;
      currentAvatar = user.avatar_url;
    }
  });

  onMount(async () => {
    const res = await api.get<{ id: string; url: string }[]>('/users/current/avatar-presets');
    presets = res ?? [];
  });

  async function choosePreset(id: string) {
    try {
      await api.put<{ status: string; message: string; avatar_url: string }>('/users/current/avatar', {
        preset: id,
      });
      await fetchCurrentUser();
      message = 'Avatar updated';
      messageType = 'success';
    } catch (err) {
      message = err instanceof ApiError ? err.message : 'An error occurred';
      messageType = 'error';
    }
  }

  async function handleSave(e: Event) {
    e.preventDefault();
    saving = true;
    message = '';
    try {
      await api.put('/users/current/profile', {
        username: username.trim(),
      });
      await fetchCurrentUser();
      message = 'Profile updated';
      messageType = 'success';
    } catch (err) {
      message = err instanceof ApiError ? err.message : 'An error occurred';
      messageType = 'error';
    } finally {
      saving = false;
    }
  }
</script>

<div class="narrow">
  <h1>Edit Profile</h1>
  {#if message}<p class={messageType === 'error' ? 'msg--error' : 'msg--success'}>{message}</p>{/if}

  {#if user}
    <section class="avatar-presets">
      <h2>Profile image</h2>
      <div class="avatar-preview">
        <Avatar username={user.username} avatarUrl={currentAvatar} size="lg" />
      </div>
      <div class="preset-grid">
        {#each presets as p (p.id)}
          <button
            type="button"
            class="preset"
            class:selected={currentAvatar === p.url}
            data-testid="avatar-preset"
            onclick={() => choosePreset(p.id)}
            aria-label={`Choose ${p.id}`}
          >
            <img src={p.url} alt={p.id} />
          </button>
        {/each}
      </div>
    </section>

    <form onsubmit={handleSave} class="card card--no-hover form-section">
      <label class="field"
        ><span class="field-label">Username</span>
        <input class="input" type="text" bind:value={username} required maxlength="30" /></label
      >
      <button class="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
    </form>
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.5rem;
  }
  .avatar-presets {
    margin-bottom: 1.5rem;
  }
  .avatar-presets h2 {
    font-size: 1rem;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }
  .avatar-preview {
    margin-bottom: 0.75rem;
  }
  .preset-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .preset {
    padding: 0;
    border: 2px solid transparent;
    border-radius: 50%;
    background: none;
    cursor: pointer;
    transition: border-color 0.15s;
  }
  .preset img {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: block;
  }
  .preset:hover {
    border-color: var(--accent, #6c63ff);
  }
  .preset.selected {
    border-color: var(--accent, #6c63ff);
  }
  .form-section {
    padding: 1rem;
  }
  .field {
    display: block;
    margin-bottom: 0.75rem;
  }
  .field-label {
    display: block;
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.25rem;
  }
</style>
